import React from 'react';
import { MapPin, Heart, User, Hand } from 'lucide-react';
import { PREFERENCE_OPTIONS } from '../../constants';
import FoundingMemberBadge from '../FoundingMemberBadge';

// Compact reactions toolbar at the top of the profile detail card.
// Combines:
//  - Heart (Like) with live count
//  - Finger/Hand (Poke)
//  - small "Online now" badge
const ReactionsToolbar = ({
  liked, totalLikes, onLike, onPoke, actionLoading, isOnline,
}) => (
  <div className="flex items-center justify-end gap-2" data-testid="reactions-toolbar">
    {isOnline && (
      <span
        className="inline-flex items-center gap-1.5 bg-green-500/15 border border-green-400/40 text-green-200 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
        data-testid="profile-online-pill"
      >
        <span className="relative flex items-center justify-center w-2 h-2">
          <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75"></span>
          <span className="relative w-2 h-2 rounded-full bg-green-400" />
        </span>
        Live
      </span>
    )}
    <button
      onClick={onPoke}
      disabled={actionLoading === 'poke'}
      type="button"
      aria-label="Poke this profile"
      title="Poke"
      className="w-14 h-14 rounded-full bg-fnl-purple-light/60 hover:bg-fnl-purple-light border-2 border-purple-700 hover:border-fnl-pink/60 text-fnl-pink flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-60 shadow-md"
      data-testid="profile-poke-top-btn"
    >
      <Hand className="w-7 h-7" strokeWidth={2} />
    </button>
    <button
      onClick={onLike}
      disabled={actionLoading === 'like'}
      type="button"
      aria-label={liked ? 'Unlike this profile' : 'Like this profile'}
      title={liked ? 'Unlike' : 'Like'}
      className="relative w-16 h-14 flex items-center justify-center transition-transform duration-200 hover:scale-110 active:scale-95 disabled:opacity-60"
      data-testid="profile-like-btn"
    >
      <Heart
        className={`w-16 h-14 transition-colors duration-300 ${liked ? 'fill-red-500 text-red-500' : 'fill-white text-white drop-shadow-md'}`}
        strokeWidth={1.5}
      />
      <span
        className={`absolute inset-0 flex items-center justify-center text-sm font-extrabold pointer-events-none ${liked ? 'text-white' : 'text-fnl-pink'}`}
        style={{ paddingTop: '4px' }}
        data-testid="profile-like-count"
      >
        {totalLikes}
      </span>
    </button>
  </div>
);

const ProfilePreferences = ({ preferences }) => (
  <>
    <div className="text-sm uppercase tracking-wider text-fnl-pink mb-2 font-semibold">Interest-Into</div>
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6" data-testid="profile-preferences-grid">
      {PREFERENCE_OPTIONS.map((p) => {
        const active = preferences.includes(p);
        return (
          <div
            key={p}
            className={
              active
                ? 'px-2 py-2 rounded-lg text-center text-xs font-semibold bg-fnl-pink text-white border border-fnl-pink shadow-[0_4px_12px_rgba(238,42,123,0.35)]'
                : 'px-2 py-2 rounded-lg text-center text-xs font-medium bg-fnl-purple-light/40 text-purple-300/70 border border-purple-800/40 line-through'
            }
          >
            {p}
          </div>
        );
      })}
    </div>
  </>
);

const ProfileInfo = ({
  profile,
  isOnline,
  match,
  liked,
  totalLikes,
  actionLoading,
  onLike,
  onPoke,
  actions,
}) => (
  <>
    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-fnl-purple-light flex items-center justify-center">
      {profile.photo_base64 ? (
        <img src={profile.photo_base64} alt={profile.name} className="w-full h-full object-cover" />
      ) : (
        <User className="w-32 h-32 text-fnl-pink/50" />
      )}
      {match && (
        <div className="absolute bottom-4 left-4 right-4 bg-fnl-pink/95 backdrop-blur-sm rounded-xl px-3 py-2 text-center text-white text-sm font-bold flex items-center justify-center gap-1.5" data-testid="profile-match-badge">
          <Heart className="w-4 h-4 fill-white" /> It&apos;s a Match!
        </div>
      )}
    </div>

    <div className="flex flex-col">
      <ReactionsToolbar
        liked={liked}
        totalLikes={totalLikes}
        onLike={onLike}
        onPoke={onPoke}
        actionLoading={actionLoading}
        isOnline={isOnline}
      />
      <h1 className="text-4xl font-extrabold text-white mt-2 flex items-center gap-2 flex-wrap" data-testid="profile-name">
        <span>{profile.name}, <span className="text-fnl-pink">{profile.age}</span></span>
        {profile.founding_member && <FoundingMemberBadge size="md" />}
      </h1>
      {(profile.location || profile.relationship_status) && (
        <div className="flex items-center gap-3 text-purple-200 mb-5 mt-1 flex-wrap">
          {profile.location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />{profile.location}
            </div>
          )}
          {profile.relationship_status && (
            <span
              className="px-2.5 py-0.5 rounded-full text-xs uppercase font-bold tracking-wider bg-fnl-pink/20 text-fnl-pink border border-fnl-pink/40"
              data-testid="profile-status-pill"
            >
              {profile.relationship_status}
            </span>
          )}
        </div>
      )}

      <div className="text-sm uppercase tracking-wider text-fnl-pink mb-2 font-semibold">About me</div>
      <p className="text-purple-100 leading-relaxed mb-6 whitespace-pre-line">{profile.bio || 'No bio yet.'}</p>

      <ProfilePreferences preferences={profile.preferences || []} />

      {profile.interests?.length > 0 && (
        <>
          <div className="text-sm uppercase tracking-wider text-fnl-pink mb-2 font-semibold">Interests</div>
          <div className="flex flex-wrap gap-2 mb-6">
            {profile.interests.map((i) => (
              <span key={i} className="px-3 py-1 rounded-full bg-fnl-pink/20 text-fnl-pink border border-fnl-pink/40 text-sm">{i}</span>
            ))}
          </div>
        </>
      )}

      {actions}
    </div>
  </>
);

export default ProfileInfo;
